import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ApiError, callFn, currentSlug, fetchAccount, isAuthed, isGuest as apiIsGuest, logout as apiLogout, startGuest } from '../lib/api';
import type { AccountMe } from '../lib/api';
import type { MyProfile } from '../lib/types';

type ToastKind = 'ok' | 'err' | 'info';
export interface Toast { id: number; kind: ToastKind; text: string; }

interface AppState {
  /** Session bootstrap finished (signed in or not). */
  ready: boolean;
  /** Any session at all — a member OR a guest — with a usable profile. */
  authed: boolean;
  /** The current session is an anonymous guest (POST /auth/guest). */
  isGuest: boolean;
  /** A real account (not a guest) with a usable profile. */
  signedIn: boolean;
  /** Platform identity (email, name, phone) for prefilling checkout. */
  account: AccountMe | null;
  /**
   * Make sure this browser has a session for the current store: mint a guest
   * and bind it to the store when there is none. Single-flight. Guest
   * checkout (PLAN_GUEST_CHECKOUT G-P3): called on first cart / chat action.
   */
  ensureSession: () => Promise<boolean>;
  /** The signed-in user's bound store profile (null when signed out or unbound). */
  me: MyProfile | null;
  /** Set when a valid session has no usable profile (unbound / revoked). */
  meError: string | null;
  isOwner: boolean;
  reload: () => Promise<void>;
  signOut: () => void;
  toast: (text: string, kind?: ToastKind) => void;
  toasts: Toast[];
}

const Ctx = createContext<AppState | null>(null);
export function useApp(): AppState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useApp must be used within AppProvider');
  return v;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [ready, setReady] = useState(false);
  const [me, setMe] = useState<MyProfile | null>(null);
  const [meError, setMeError] = useState<string | null>(null);
  const [account, setAccount] = useState<AccountMe | null>(null);
  const [guest, setGuest] = useState<boolean>(() => apiIsGuest());
  const ensuring = useRef<Promise<boolean> | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastId = useRef(1);
  // Sessions are per store (lib/api keys tokens by the URL slug): re-bootstrap
  // whenever the store in the URL changes.
  const { pathname } = useLocation();
  const slug = pathname.split('/')[1] === currentSlug() ? currentSlug() : '';

  const toast = useCallback((text: string, kind: ToastKind = 'info') => {
    const id = toastId.current++;
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3800);
  }, []);

  const reload = useCallback(async () => {
    setGuest(apiIsGuest());
    if (!isAuthed()) { setMe(null); setMeError(null); setAccount(null); setReady(true); return; }
    try {
      let r: { profile: MyProfile };
      try {
        r = await callFn<{ profile: MyProfile }>('get_my_profile');
      } catch (e) {
        // A guest that never got bound (e.g. the join call was interrupted):
        // bind it now, once, then read the profile again.
        if (apiIsGuest() && currentSlug() && e instanceof ApiError && e.status !== 401) {
          await callFn('join_store', { slug: currentSlug() });
          r = await callFn<{ profile: MyProfile }>('get_my_profile');
        } else throw e;
      }
      setMe(r.profile); setMeError(null);
      setAccount(await fetchAccount());
    } catch (e) {
      setMe(null);
      const status = e instanceof ApiError ? e.status : undefined;
      const msg = e instanceof Error ? e.message : 'Could not load your account.';
      if (status === 401) { apiLogout(); setGuest(false); setMeError(null); }
      else setMeError(msg);
    } finally {
      setReady(true);
    }
  }, []);

  const signOut = useCallback(() => { apiLogout(); setMe(null); setMeError(null); setAccount(null); setGuest(false); }, []);

  const ensureSession = useCallback((): Promise<boolean> => {
    if (isAuthed() && me) return Promise.resolve(true);
    if (!ensuring.current) {
      ensuring.current = (async () => {
        try {
          const s = currentSlug();
          if (!s) return false;
          await startGuest();
          try { await callFn('join_store', { slug: s }); }
          catch (e) { if (!/another store/i.test(e instanceof Error ? e.message : '')) throw e; }
          await reload();
          return true;
        } catch (e) {
          toast(e instanceof Error ? e.message : 'Could not start your session.', 'err');
          return false;
        } finally { ensuring.current = null; }
      })();
    }
    return ensuring.current;
  }, [me, reload, toast]);

  useEffect(() => { void reload(); }, [reload, slug]);

  const authed = isAuthed() && !!me;
  return (
    <Ctx.Provider value={{
      ready, authed, isGuest: authed && guest, signedIn: authed && !guest, me, meError, account,
      ensureSession, isOwner: me?.role === 'owner', reload, signOut, toast, toasts,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export function Toasts() {
  const { toasts } = useApp();
  if (toasts.length === 0) return null;
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => <div key={t.id} className={`toast toast-${t.kind}`}>{t.text}</div>)}
    </div>
  );
}
